import type {
  IAiSpreadsheetLoadedSheet,
  IAiSpreadsheetRegionOverrideCandidate,
  IAiSpreadsheetRegionOverrideReview,
} from "@/payroll/ai-script/types";
import type { MutationErrorHandlers } from "./mutation-error-feedback";
import type { SpreadsheetRegionDraft } from "./spreadsheet-region";
import { emptySpreadsheetRegionViewState } from "./spreadsheet-region-view";
import type { ViewerMutations } from "./use-viewer-mutations";
import type { ViewerUiState } from "./use-viewer-ui-state";
import { emptySelection } from "./utils";

interface CreateRegionHandlersParams {
  runId: string;
  activeLoadedSheet: IAiSpreadsheetLoadedSheet | null;
  activeSheetValue: string;
  canMutateActiveSheet: boolean;
  handleMutationError: MutationErrorHandlers["handleMutationError"];
  mutations: Pick<
    ViewerMutations,
    | "createSpreadsheetRegion"
    | "updateSpreadsheetRegion"
    | "deleteSpreadsheetRegion"
    | "confirmSpreadsheetRegionOverride"
  >;
  ui: Pick<
    ViewerUiState,
    | "setRegionViewStatesBySheet"
    | "setSpreadsheetColumnMenu"
    | "setGridSelection"
  >;
}

export const createRegionHandlers = ({
  runId,
  activeLoadedSheet,
  activeSheetValue,
  canMutateActiveSheet,
  handleMutationError,
  mutations: {
    createSpreadsheetRegion,
    updateSpreadsheetRegion,
    deleteSpreadsheetRegion,
    confirmSpreadsheetRegionOverride,
  },
  ui: {
    setRegionViewStatesBySheet,
    setSpreadsheetColumnMenu,
    setGridSelection,
  },
}: CreateRegionHandlersParams) => {
  const changeActiveRegionView = (
    regionId: string,
    state: ReturnType<typeof emptySpreadsheetRegionViewState>,
  ) => {
    setRegionViewStatesBySheet((current) => ({
      ...current,
      [activeSheetValue]: {
        ...(current[activeSheetValue] ?? {}),
        [regionId]: state,
      },
    }));
    setGridSelection(emptySelection);
  };

  const clearActiveRegionViews = () => {
    setRegionViewStatesBySheet((current) => ({
      ...current,
      [activeSheetValue]: {},
    }));
    setSpreadsheetColumnMenu(null);
    setGridSelection(emptySelection);
  };

  const createRegion = async (region: SpreadsheetRegionDraft) => {
    if (!activeLoadedSheet || !canMutateActiveSheet) return;
    try {
      await createSpreadsheetRegion.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        region,
      });
    } catch (mutationError) {
      handleMutationError("Failed to create table", mutationError);
      throw mutationError;
    }
  };

  const updateRegion = async (
    regionId: string,
    region: SpreadsheetRegionDraft,
  ) => {
    if (!activeLoadedSheet || !canMutateActiveSheet) return;
    try {
      await updateSpreadsheetRegion.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        regionId,
        region,
      });
      setRegionViewStatesBySheet((current) => ({
        ...current,
        [activeSheetValue]: {
          ...(current[activeSheetValue] ?? {}),
          [regionId]: emptySpreadsheetRegionViewState(),
        },
      }));
    } catch (mutationError) {
      handleMutationError("Failed to update table", mutationError);
      throw mutationError;
    }
  };

  const deleteRegion = async (regionId: string) => {
    if (!activeLoadedSheet || !canMutateActiveSheet) return;
    try {
      await deleteSpreadsheetRegion.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        regionId,
      });
      setRegionViewStatesBySheet((current) => ({
        ...current,
        [activeSheetValue]: Object.fromEntries(
          Object.entries(current[activeSheetValue] ?? {}).filter(
            ([id]) => id !== regionId,
          ),
        ),
      }));
    } catch (mutationError) {
      handleMutationError("Failed to remove table", mutationError);
      throw mutationError;
    }
  };

  const confirmRegionOverride = async (
    review: IAiSpreadsheetRegionOverrideReview,
    candidate: IAiSpreadsheetRegionOverrideCandidate,
  ) => {
    if (!activeLoadedSheet) return;
    try {
      await confirmSpreadsheetRegionOverride.mutateAsync({
        runId,
        sheetId: activeLoadedSheet.id,
        overrideId: review.id,
        candidate,
      });
      clearActiveRegionViews();
    } catch (mutationError) {
      handleMutationError("Failed to confirm saved table", mutationError);
    }
  };

  return {
    changeActiveRegionView,
    clearActiveRegionViews,
    createRegion,
    updateRegion,
    deleteRegion,
    confirmRegionOverride,
  };
};
